import React, { useMemo } from "react";
import {
  Alert,
  Box,
  Checkbox,
  FormControl,
  FormControlLabel,
  FormGroup,
  FormHelperText,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  Switch,
  TextField,
  Typography,
} from "@mui/material";
import ScheduleOutlinedIcon from "@mui/icons-material/ScheduleOutlined";
import { useTranslation } from "react-i18next";
import {
  DEFAULT_FAQ_BUSINESS_HOURS,
  FAQ_TIMEZONE_OPTIONS,
  FAQ_WEEKDAYS,
  getFaqOutsideHoursMessage,
  isFaqBusinessHoursEnabled,
  normalizeFaqBusinessHours,
  toTimeInputValue,
} from "../../utils/faqSkillConfig";

/**
 * Horário comercial e mensagem fora do expediente (config FAQ → faqSkill.js).
 */
export default function FaqBusinessHoursSection({ skillConfig = {}, onChange, disabled = false, embedded = false }) {
  const { t } = useTranslation("settings");
  const enabled = isFaqBusinessHoursEnabled(skillConfig);
  const businessHours = useMemo(
    () => normalizeFaqBusinessHours(skillConfig?.business_hours),
    [skillConfig?.business_hours]
  );
  const outsideMessage = getFaqOutsideHoursMessage(skillConfig);

  const patch = (partial) => onChange?.(partial);

  const setEnabled = (on) => {
    if (!on) {
      patch({ business_hours: null, outside_hours_message: "", out_of_hours_message: null });
      return;
    }
    patch({
      business_hours: { ...DEFAULT_FAQ_BUSINESS_HOURS },
      outside_hours_message: outsideMessage || "",
    });
  };

  const updateBusinessHours = (partial) => {
    patch({
      business_hours: { ...businessHours, ...partial },
    });
  };

  const toggleWeekday = (day) => {
    const set = new Set(businessHours.weekdays || []);
    if (set.has(day)) set.delete(day);
    else set.add(day);
    updateBusinessHours({ weekdays: [...set].sort((a, b) => a - b) });
  };

  return (
    <Box>
      {!embedded ? (
        <>
          <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1 }}>
            <ScheduleOutlinedIcon fontSize="small" color="action" />
            <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
              {t("agentsListPage.faqBusinessHoursTitle")}
            </Typography>
          </Stack>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
            {t("agentsListPage.faqBusinessHoursIntro")}
          </Typography>
        </>
      ) : null}

      <FormControlLabel
        control={
          <Switch checked={enabled} onChange={(e) => setEnabled(e.target.checked)} disabled={disabled} />
        }
        label={t("agentsListPage.faqBusinessHoursEnable")}
        sx={{ mb: enabled ? 2 : 0 }}
      />

      {enabled && (
        <Stack spacing={2}>
          <FormControl fullWidth size="small" disabled={disabled}>
            <InputLabel>{t("agentsListPage.faqBusinessHoursTimezone")}</InputLabel>
            <Select
              label={t("agentsListPage.faqBusinessHoursTimezone")}
              value={businessHours.timezone}
              onChange={(e) => updateBusinessHours({ timezone: e.target.value })}
            >
              {FAQ_TIMEZONE_OPTIONS.map((tz) => (
                <MenuItem key={tz.value} value={tz.value}>
                  {t(tz.labelKey)}
                </MenuItem>
              ))}
            </Select>
            <FormHelperText>{t("agentsListPage.faqBusinessHoursTimezoneHelper")}</FormHelperText>
          </FormControl>

          <Box>
            <Typography variant="caption" color="text.secondary" sx={{ display: "block", mb: 0.75 }}>
              {t("agentsListPage.faqBusinessHoursWeekdays")}
            </Typography>
            <FormGroup row>
              {FAQ_WEEKDAYS.map(({ value, labelKey }) => (
                <FormControlLabel
                  key={value}
                  control={
                    <Checkbox
                      size="small"
                      checked={(businessHours.weekdays || []).includes(value)}
                      onChange={() => toggleWeekday(value)}
                      disabled={disabled}
                    />
                  }
                  label={t(labelKey)}
                />
              ))}
            </FormGroup>
          </Box>

          <Stack direction={{ xs: "column", sm: "row" }} spacing={2}>
            <TextField
              label={t("agentsListPage.faqBusinessHoursStart")}
              type="time"
              value={toTimeInputValue(businessHours.start)}
              onChange={(e) => updateBusinessHours({ start: e.target.value })}
              InputLabelProps={{ shrink: true }}
              fullWidth
              size="small"
              disabled={disabled}
            />
            <TextField
              label={t("agentsListPage.faqBusinessHoursEnd")}
              type="time"
              value={toTimeInputValue(businessHours.end)}
              onChange={(e) => updateBusinessHours({ end: e.target.value })}
              InputLabelProps={{ shrink: true }}
              fullWidth
              size="small"
              disabled={disabled}
            />
          </Stack>

          <TextField
            label={t("agentsListPage.faqOutsideHoursMessage")}
            helperText={t("agentsListPage.faqOutsideHoursMessageHelper")}
            placeholder={t("agentsListPage.faqOutsideHoursMessagePlaceholder")}
            value={outsideMessage}
            onChange={(e) =>
              patch({
                outside_hours_message: e.target.value,
                out_of_hours_message: null,
              })
            }
            multiline
            minRows={3}
            fullWidth
            size="small"
            required
            disabled={disabled}
          />

          <Alert severity="info" sx={{ py: 0.75 }}>
            {t("agentsListPage.faqBusinessHoursLifecycleHint")}
          </Alert>
        </Stack>
      )}
    </Box>
  );
}
